import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, STATUS_ORDER, CATEGORY_META, CATEGORY_ORDER, fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import QuickAssign from "@/components/task/QuickAssign";
import TaskDialog from "@/components/TaskDialog";
import { Input } from "@/components/ui/input";
import { Search, MessageSquare, Paperclip, X, Bookmark, Plus, Trash2, Globe, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function TasksView() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dialogTask, setDialogTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [views, setViews] = useState([]);

  const loadViews = useCallback(() => api.get("/views").then((r) => setViews(r.data)).catch(() => {}), []);
  useEffect(() => { loadViews(); }, [loadViews]);

  const saveView = async () => {
    const name = window.prompt("Name this view (e.g. My overdue, This builder):");
    if (!name || !name.trim()) return;
    const filters = { status, assignee, category, q };
    try { await api.post("/views", { name: name.trim(), filters }); loadViews(); }
    catch (e) { toast.error(apiErr(e, "Couldn't save this view")); }
  };
  const applyView = (v) => {
    const f = v.filters || {};
    const next = new URLSearchParams();
    if (f.status && f.status !== "all") next.set("status", f.status);
    if (f.assignee && f.assignee !== "all") next.set("assignee", f.assignee);
    if (f.category && f.category !== "all") next.set("category", f.category);
    setParams(next);
    setQ(f.q || "");
  };
  const deleteView = async (id) => {
    try { await api.delete(`/views/${id}`); loadViews(); } catch (e) { toast.error("Couldn't delete this view"); }
  };
  const toggleShare = async (v) => {
    try {
      await api.patch(`/views/${v.view_id}`, { shared: !v.shared });
      loadViews();
      toast.success(v.shared ? "View is now private" : "Shared with the team");
    } catch (e) { toast.error("Couldn't update sharing"); }
  };

  const status = params.get("status") || "all";
  const assignee = params.get("assignee") || "all";
  const category = params.get("category") || "all";

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const query = {};
    if (status !== "all") query.status = status;
    if (assignee !== "all") query.assignee = assignee;
    if (category !== "all") query.category = category;
    if (q.trim()) query.q = q.trim();
    const [t, u] = await Promise.all([
      api.get("/tasks", { params: query }),
      api.get("/users"),
    ]);
    setTasks(t.data);
    setMembers(u.data);
    setLoading(false);
  }, [status, assignee, category, q]);

  useEffect(() => {
    const id = setTimeout(load, q ? 300 : 0);
    return () => clearTimeout(id);
  }, [load, q]);

  const memberMap = Object.fromEntries(members.map((m) => [m.user_id, m]));
  const openTask = (t) => { setDialogTask(t); setDialogOpen(true); };
  const canManage = user?.role === "admin" || user?.can_manage;
  const clearAll = () => { setParams(new URLSearchParams()); setQ(""); };
  const hasFilters = status !== "all" || assignee !== "all" || category !== "all" || q;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-1">Everything</p>
        <h1 className="font-heading text-4xl font-black tracking-tighter">All Tasks</h1>
        <p className="text-zinc-500 mt-2 text-sm">Search and filter every task across all projects.</p>
      </header>

      {/* Filter bar */}
      <div className="bg-white border border-zinc-200 p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
          <Search className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="task-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search tasks by name…" className="pl-9 rounded-sm" />
        </div>

        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger className="w-full sm:w-[160px] h-11 sm:h-10 rounded-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={assignee} onValueChange={(v) => setParam("assignee", v)}>
          <SelectTrigger className="w-full sm:w-[170px] h-11 sm:h-10 rounded-sm" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Anyone</SelectItem>
            {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="w-full sm:w-[190px] h-11 sm:h-10 rounded-sm" data-testid="filter-category"><SelectValue placeholder="Folder" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All folders</SelectItem>
            {CATEGORY_ORDER.map((c) => <SelectItem key={c} value={c}>{CATEGORY_META[c].label} · {CATEGORY_META[c].sub}</SelectItem>)}
          </SelectContent>
        </Select>

        {hasFilters && (
          <button onClick={clearAll} className="text-sm text-zinc-500 hover:text-zinc-900 flex items-center gap-1 min-h-[40px] px-2" data-testid="clear-filters">
            <X className="h-4 w-4" /> Clear
          </button>
        )}
        {hasFilters && (
          <button onClick={saveView} className="text-sm font-semibold text-zinc-900 hover:underline flex items-center gap-1 min-h-[40px] px-2" data-testid="save-view">
            <Plus className="h-4 w-4" /> Save view
          </button>
        )}
      </div>

      {views.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="saved-views">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-1"><Bookmark className="h-3.5 w-3.5" /> Saved:</span>
          {views.map((v) => (
            <span key={v.view_id} className="group inline-flex items-center gap-1 rounded-full bg-zinc-100 hover:bg-zinc-200 transition-colors pl-3 pr-1 py-1" data-testid={`saved-view-${v.view_id}`}>
              <button onClick={() => applyView(v)} className="text-xs font-medium text-zinc-800 flex items-center gap-1">
                {v.shared && <Globe className="h-3 w-3 text-emerald-600" />}
                {v.name}
                {!v.is_mine && <span className="text-[10px] text-zinc-400">· {v.owner_name}</span>}
              </button>
              {v.is_mine && (
                <button onClick={() => toggleShare(v)} title={v.shared ? "Make private" : "Share with team"}
                  className={`p-0.5 ${v.shared ? "text-emerald-600" : "text-zinc-400 hover:text-emerald-600"}`}
                  data-testid={`share-view-${v.view_id}`}>
                  {v.shared ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                </button>
              )}
              {(v.is_mine || user?.role === "admin") && (
                <button onClick={() => deleteView(v.view_id)} className="text-zinc-400 hover:text-red-600 p-0.5" data-testid={`delete-view-${v.view_id}`}><Trash2 className="h-3 w-3" /></button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="bg-white border border-zinc-200">
        <div className="px-5 py-3 border-b border-zinc-200 text-xs text-zinc-500 flex items-center justify-between">
          <span>{loading ? "Loading…" : `${tasks.length} task${tasks.length !== 1 ? "s" : ""}`}</span>
        </div>
        {!loading && tasks.length === 0 && <p className="p-6 text-sm text-zinc-400">No tasks match your filters.</p>}
        {tasks.map((t) => (
          <div key={t.task_id} role="button" tabIndex={0}
            onClick={() => openTask(t)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openTask(t); } }}
            className="w-full grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_180px_90px_90px_130px] gap-3 items-center px-4 sm:px-5 py-4 md:py-3 min-h-[60px] md:min-h-0 border-b border-zinc-100 last:border-0 hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left cursor-pointer"
            data-testid={`tasks-row-${t.task_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate flex items-center gap-2">
                {t.name}
                {t.comment_count > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400"><MessageSquare className="h-3 w-3" />{t.comment_count}</span>}
                {t.attachments?.length > 0 && <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400"><Paperclip className="h-3 w-3" />{t.attachments.length}</span>}
              </p>
              <p className="text-xs text-zinc-400 truncate mt-0.5">
                {t.builder ? `${t.builder} · ` : ""}{t.project_name}
                {t.category && <span className="ml-1">· {CATEGORY_META[t.category]?.label} {CATEGORY_META[t.category]?.sub}</span>}
              </p>
            </div>
            <span className="hidden md:flex" onClick={(e) => e.stopPropagation()}>
              <QuickAssign task={t} members={members} memberMap={memberMap} onSaved={load} />
            </span>
            <span className={`hidden md:block text-xs ${t.overdue ? "text-rose-600 font-semibold" : "text-zinc-500"}`}>{fmtDate(t.end_date)}</span>
            <span className="hidden md:block"></span>
            <span className="justify-self-end"><StatusBadge status={t.status} /></span>
          </div>
        ))}
      </div>

      <TaskDialog
        open={dialogOpen} onOpenChange={setDialogOpen}
        task={dialogTask} projectId={dialogTask?.project_id}
        members={members} canManage={canManage} currentUser={user}
        onSaved={load}
      />
    </div>
  );
}
